import React, { useState, useEffect } from 'react';
import { Link, Element } from 'react-scroll';

const HomeSection = () => {
  // State for tracking window width
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  
  // Determine if mobile based on window width
  const isMobile = windowWidth < 768;
  
  // Add event listener for window resize
  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }
    
    // Add event listener
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      
      // Call handler right away so state gets updated with initial window size
      handleResize();
      
      // Remove event listener on cleanup
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []); // Empty array ensures effect runs only on mount and unmount
  
  // Define all styles inline
  const styles = {
    section: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      overflow: 'hidden',
      padding: isMobile ? '60px 0' : '0',
      background: 'linear-gradient(145deg, #f8fafc 0%, #f0f7ff 100%)',
    },
    container: {
      width: '100%',
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 20px',
      display: 'flex',
      flexDirection: isMobile ? 'column-reverse' : 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: isMobile ? '40px' : '60px',
    },
    imageColumn: {
      flex: isMobile ? '1 1 100%' : '1 1 55%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
    },
    mobileAppContainer: {
      position: 'relative',
      padding: '20px',
      zIndex: 2,
    },
    mobileAppImage: {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '24px',
      boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15)',
      transform: isMobile ? 'none' : 'perspective(1000px) rotateY(5deg) rotateX(2deg)',
      transition: 'transform 0.5s ease',
    },
    decorCircle1: {
      position: 'absolute',
      width: '300px',
      height: '300px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, rgba(30, 80, 162, 0.1) 0%, rgba(30, 80, 162, 0.03) 100%)',
      top: '-30px',
      left: '-30px',
      zIndex: 1,
    },
    decorCircle2: {
      position: 'absolute',
      width: '200px',
      height: '200px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, rgba(30, 80, 162, 0.08) 0%, rgba(30, 80, 162, 0.02) 100%)',
      bottom: '40px',
      right: '20px',
      zIndex: 1,
    },
    decorDots: {
      position: 'absolute',
      width: '120px',
      height: '120px',
      backgroundImage: 'radial-gradient(circle, #1e50a2 2px, transparent 2px)',
      backgroundSize: '20px 20px',
      opacity: 0.2,
      zIndex: 1,
      top: '30%',
      right: '-40px',
    },
    contentColumn: {
      flex: isMobile ? '1 1 100%' : '1 1 45%',
      paddingRight: isMobile ? '0' : '40px',
      textAlign: isMobile ? 'center' : 'left',
    },
    contentHighlight: {
      fontSize: '16px',
      color: '#1e50a2',
      fontWeight: '600',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      justifyContent: isMobile ? 'center' : 'flex-start',
    },
    highlightLine: {
      width: '30px',
      height: '2px',
      backgroundColor: '#1e50a2',
    },
    title: {
      fontSize: isMobile ? '42px' : '56px',
      fontWeight: 'bold',
      lineHeight: 1.1,
      backgroundImage: 'linear-gradient(135deg, #0a1438 0%, #1e50a2 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      color: 'transparent',
      marginBottom: '24px',
      letterSpacing: '-0.5px',
    },
    titleHighlight: {
      color: '#1e50a2',
      position: 'relative',
      display: 'inline-block',
    },
    description: {
      fontSize: '18px',
      lineHeight: 1.7,
      color: '#4a5568',
      marginBottom: '32px',
      maxWidth: '500px',
      margin: isMobile ? '0 auto 32px' : '0 0 32px',
    },
    ctaContainer: {
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
      justifyContent: isMobile ? 'center' : 'flex-start',
    },
    primaryButton: {
      padding: '14px 32px',
      fontSize: '16px',
      fontWeight: 'bold',
      backgroundColor: '#1e50a2',
      color: 'white',
      border: 'none',
      borderRadius: '30px',
      cursor: 'pointer',
      boxShadow: '0 10px 20px rgba(30, 80, 162, 0.3)',
      transition: 'all 0.3s ease',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
    },
    primaryButtonHover: {
      backgroundColor: '#0a1438',
      transform: 'translateY(-3px)',
      boxShadow: '0 15px 25px rgba(30, 80, 162, 0.4)',
    },
    secondaryButton: {
      padding: '14px 32px',
      fontSize: '16px',
      fontWeight: 'bold',
      backgroundColor: 'white',
      color: '#1e50a2',
      border: '1px solid #e2e8f0',
      borderRadius: '30px',
      cursor: 'pointer',
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
    },
    secondaryButtonHover: {
      backgroundColor: '#f8fafc',
      borderColor: '#cbd5e0',
      transform: 'translateY(-3px)',
      boxShadow: '0 10px 20px rgba(0, 0, 0, 0.08)',
    },
    statsBadges: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '16px',
      marginTop: '40px',
      justifyContent: isMobile ? 'center' : 'flex-start',
    },
    statBadge: {
      backgroundColor: 'white',
      padding: '12px 20px',
      borderRadius: '12px',
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    statIcon: {
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      backgroundColor: '#f0f7ff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#1e50a2',
      fontSize: '12px',
    },
    statText: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#4a5568',
    },
    statValue: {
      fontWeight: 'bold',
      color: '#0a1438',
    },
  };

  // State for hover effects
  const [hoveredButton, setHoveredButton] = useState(null);
  
  return (
    <section id="home" style={styles.section}>
      <div style={styles.container}>
        {/* Left side - Mobile App Image */}
        <div style={styles.imageColumn}>
          <div style={styles.decorCircle1}></div>
          <div style={styles.decorCircle2}></div>
          <div style={styles.decorDots}></div>
          <div style={styles.mobileAppContainer}>
            <img 
              src={`${process.env.PUBLIC_URL}/images/app.png`} 
              alt="Laki mobile app interface" 
              style={styles.mobileAppImage}
              onError={(e) => {
                e.target.onerror = null; 
                e.target.src = `https://via.placeholder.com/300x600?text=Laki+App`;
              }}
            />
          </div>
        </div>

        {/* Right side - Content */}
        <div style={styles.contentColumn}>
          <div style={styles.contentHighlight}>
            <span style={styles.highlightLine}></span>
            <span>Welcome to Laki</span>
          </div>
          <h1 style={styles.title}>
            Smart <br />
            <span style={styles.titleHighlight}>Investments</span> <br />
            Bright Futures.
          </h1>
          <p style={styles.description}>
            Meet Laki. The all-in-one platform for money market funds across Africa.
            Created for all kinds of financial goals and investors at every level.
          </p>
          
          <div style={styles.ctaContainer}>
            <Link 
              to="services"
              smooth={true}
              duration={1000}
              style={{
                ...styles.primaryButton, 
                ...(hoveredButton === 'primary' ? styles.primaryButtonHover : {})
              }}
              onMouseEnter={() => setHoveredButton('primary')}
              onMouseLeave={() => setHoveredButton(null)}
            >
              Get Started 
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 5L19 12L12 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            
            <Link 
              to="about"
              smooth={true}
              duration={1000}
              style={{
                ...styles.secondaryButton, 
                ...(hoveredButton === 'secondary' ? styles.secondaryButtonHover : {})
              }}
              onMouseEnter={() => setHoveredButton('secondary')}
              onMouseLeave={() => setHoveredButton(null)}
            >
              Learn More
            </Link>
          </div>
          
          <div style={styles.statsBadges}>
            <div style={styles.statBadge}>
              <div style={styles.statIcon}>✓</div>
              <span style={styles.statText}>
                <span style={styles.statValue}>10,000+</span> Users
              </span>
            </div>
            <div style={styles.statBadge}>
              <div style={styles.statIcon}>⭐</div>
              <span style={styles.statText}>
                <span style={styles.statValue}>4.8</span> App Store Rating
              </span>
            </div>
            <div style={styles.statBadge}>
              <div style={styles.statIcon}>$</div>
              <span style={styles.statText}>
                <span style={styles.statValue}>$100</span> Min Investment
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeSection;